import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Zap, 
  Activity,
  Target,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  PieChart,
  Download,
  DollarSign
} from 'lucide-react';

interface AnalyticsData {
  network_stats: {
    total_transactions: number;
    daily_transactions: number;
    total_users: number;
    active_users: number;
    total_volume: number;
    daily_volume: number;
    avg_gas_price: number;
    network_utilization: number;
  };
  token_stats: {
    vttn_price: number;
    price_change_24h: number;
    market_cap: number;
    total_supply: number;
    circulating_supply: number;
    holders: number;
  };
  staking_stats: {
    total_staked: number;
    staking_ratio: number;
    validators: number;
    avg_apy: number;
  };
  bridge_stats: {
    total_bridged: number;
    daily_bridge_volume: number;
    supported_chains: number;
    bridge_transactions: number;
  };
}

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string;
    borderColor?: string;
    fill?: boolean;
  }[];
}

const AnalyticsPage: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');
  const [activeChart, setActiveChart] = useState('transactions');

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`http://localhost:3600/api/analytics?range=${timeRange}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAnalyticsData(data.data);
        }
      }
    } catch (error) {
      console.error('분석 데이터 가져오기 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    // Chart data fetching logic if needed
  };

  useEffect(() => {
    fetchAnalyticsData();
    fetchChartData();
  }, [timeRange]);

  // 더미 데이터 설정 (로딩 완료 후 데이터가 없을 때)
  useEffect(() => {
    if (!loading && !analyticsData) {
      setAnalyticsData({
        network_stats: {
          total_transactions: 2847230,
          daily_transactions: 12450,
          total_users: 45678,
          active_users: 3247,
          total_volume: 125000000,
          daily_volume: 2450000,
          avg_gas_price: 15.5,
          network_utilization: 67.8
        },
        token_stats: {
          vttn_price: 0.5,
          price_change_24h: 12.45,
          market_cap: 350000000,
          total_supply: 1000000000,
          circulating_supply: 700000000,
          holders: 23456
        },
        staking_stats: {
          total_staked: 450000000,
          staking_ratio: 64.3,
          validators: 47,
          avg_apy: 12.8
        },
        bridge_stats: {
          total_bridged: 25000000,
          daily_bridge_volume: 125000,
          supported_chains: 4,
          bridge_transactions: 8934
        }
      });
    }
  }, [loading, analyticsData]);

  // fetchChartData는 위에서 이미 정의됨

  const formatNumber = (num: number) => {
    if (num >= 1000000000) {
      return (num / 1000000000).toFixed(1) + 'B';
    }
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const exportData = () => {
    if (analyticsData) {
      const dataStr = JSON.stringify(analyticsData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `voltron-analytics-${timeRange}-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
    }
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
              <PieChart className="w-8 h-8 text-orange-500" />
              <span>네트워크 분석</span>
            </h1>
            <p className="text-gray-600 mt-1">
              Voltron 메인넷의 상세한 통계와 분석 데이터를 확인하세요
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="1d">24시간</option>
              <option value="7d">7일</option>
              <option value="30d">30일</option>
              <option value="90d">90일</option>
            </select>
            <button
              onClick={exportData}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>내보내기</span>
            </button>
            <button
              onClick={() => {
                fetchAnalyticsData();
                fetchChartData();
              }}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              disabled={loading}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="text-gray-600 mt-4">분석 데이터를 불러오는 중...</p>
        </div>
      ) : analyticsData && (
        <>
          {/* 주요 지표 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* 총 거래량 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">총 거래량</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(analyticsData.network_stats.total_transactions)}
                  </p>
                  <div className="flex items-center mt-2">
                    <ArrowUpRight className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-600 ml-1">
                      +{formatNumber(analyticsData.network_stats.daily_transactions)} (24h)
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Activity className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            {/* 활성 사용자 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">활성 사용자</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatNumber(analyticsData.network_stats.active_users)}
                  </p>
                  <div className="flex items-center mt-2">
                    <ArrowUpRight className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-600 ml-1">
                      총 {formatNumber(analyticsData.network_stats.total_users)}명
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            {/* VTTN 가격 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">VTTN 가격</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${analyticsData.token_stats.vttn_price.toFixed(3)}
                  </p>
                  <div className="flex items-center mt-2">
                    {analyticsData.token_stats.price_change_24h >= 0 ? (
                      <ArrowUpRight className="w-4 h-4 text-green-500" />
                    ) : (
                      <ArrowDownRight className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-sm ml-1 ${
                      analyticsData.token_stats.price_change_24h >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {analyticsData.token_stats.price_change_24h >= 0 ? '+' : ''}
                      {analyticsData.token_stats.price_change_24h.toFixed(2)}%
                    </span>
                  </div>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <DollarSign className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>

            {/* 네트워크 사용률 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">네트워크 사용률</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analyticsData.network_stats.network_utilization.toFixed(1)}%
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div 
                      className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${analyticsData.network_stats.network_utilization}%` }}
                    ></div>
                  </div>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <Zap className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* 상세 통계 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 토큰 통계 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-orange-500" />
                <span>토큰 통계</span>
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">시가총액</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(analyticsData.token_stats.market_cap)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">총 공급량</span>
                  <span className="font-semibold text-gray-900">
                    {formatNumber(analyticsData.token_stats.total_supply)} VTTN
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">유통 공급량</span>
                  <span className="font-semibold text-gray-900">
                    {formatNumber(analyticsData.token_stats.circulating_supply)} VTTN
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">홀더 수</span>
                  <span className="font-semibold text-gray-900">
                    {formatNumber(analyticsData.token_stats.holders)}
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">
                    유통 비율: {((analyticsData.token_stats.circulating_supply / analyticsData.token_stats.total_supply) * 100).toFixed(1)}%
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${(analyticsData.token_stats.circulating_supply / analyticsData.token_stats.total_supply) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* 스테이킹 통계 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                <span>스테이킹 통계</span>
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">총 스테이킹</span>
                  <span className="font-semibold text-gray-900">
                    {formatNumber(analyticsData.staking_stats.total_staked)} VTTN
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">스테이킹 비율</span>
                  <span className="font-semibold text-gray-900">
                    {analyticsData.staking_stats.staking_ratio.toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">검증자 수</span>
                  <span className="font-semibold text-gray-900">
                    {analyticsData.staking_stats.validators}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">평균 APY</span>
                  <span className="font-semibold text-green-600">
                    {analyticsData.staking_stats.avg_apy.toFixed(1)}%
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="text-sm text-gray-600 mb-1">
                    스테이킹 참여율
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${analyticsData.staking_stats.staking_ratio}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 브릿지 & 네트워크 통계 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 브릿지 통계 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <Target className="w-5 h-5 text-purple-500" />
                <span>브릿지 통계</span>
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">총 브릿지 볼륨</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(analyticsData.bridge_stats.total_bridged)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">일일 브릿지 볼륨</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(analyticsData.bridge_stats.daily_bridge_volume)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">지원 체인</span>
                  <span className="font-semibold text-gray-900">
                    {analyticsData.bridge_stats.supported_chains}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">브릿지 거래 수</span>
                  <span className="font-semibold text-gray-900">
                    {formatNumber(analyticsData.bridge_stats.bridge_transactions)}
                  </span>
                </div>
              </div>
            </div>

            {/* 네트워크 성능 */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <Activity className="w-5 h-5 text-green-500" />
                <span>네트워크 성능</span>
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">일일 거래량</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(analyticsData.network_stats.daily_volume)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">총 거래량</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(analyticsData.network_stats.total_volume)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">평균 가스비</span>
                  <span className="font-semibold text-gray-900">
                    {analyticsData.network_stats.avg_gas_price.toFixed(1)} Gwei
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">일일 신규 사용자</span>
                  <span className="font-semibold text-green-600">
                    +{(analyticsData.network_stats.total_users * 0.05).toFixed(0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 차트 섹션 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <BarChart3 className="w-5 h-5 text-orange-500" />
                <span>트렌드 분석</span>
              </h3>
              <div className="flex space-x-2">
                {[
                  { id: 'transactions', name: '거래량' },
                  { id: 'users', name: '사용자' },
                  { id: 'volume', name: '볼륨' },
                  { id: 'price', name: '가격' }
                ].map((chart) => (
                  <button
                    key={chart.id}
                    onClick={() => {
                      setActiveChart(chart.id);
                      fetchChartData();
                    }}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      activeChart === chart.id
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {chart.name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* 차트 영역 (실제 차트 라이브러리 사용 시 여기에 구현) */}
            <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">차트 데이터</p>
                <p className="text-sm text-gray-400">
                  {activeChart} 트렌드 ({timeRange})
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;